import React from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import { Field, FieldLabel, FieldDescription } from '@/components/ui/field';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { SelectNative } from '@/components/ui/select-native';

const QUESTION_TYPES = [
  { value: 'multiple_choice', label: 'Múltipla escolha' },
  { value: 'true_false', label: 'Verdadeiro ou falso' }
];

function buildEmptyAlternatives(questionType) {
  const labels = questionType === 'true_false' ? ['Verdadeiro', 'Falso'] : ['', '', '', ''];
  return labels.map((desc, i) => ({ description: desc, is_correct: i === 0 }));
}

function buildEmptyQuestion(position) {
  return {
    description: '',
    question_type: 'multiple_choice',
    taxonomy_id: '',
    position,
    alternatives: buildEmptyAlternatives('multiple_choice')
  };
}

export default function StepQuestions({ formik, taxonomies, isEditing, editingId, submitting }) {
  const navigate = useNavigate();

  const backTarget = isEditing ? `/admin/desafio-ao-vivo/editar/nome/${editingId}` : '/admin/desafio-ao-vivo/criar/nome';

  const handleAdd = () => {
    const next = [...formik.values.questions, buildEmptyQuestion(formik.values.questions.length + 1)];
    formik.setFieldValue('questions', next);
  };

  const handleRemove = idx => {
    const next = formik.values.questions.filter((_, i) => i !== idx).map((q, i) => ({ ...q, position: i + 1 }));
    formik.setFieldValue('questions', next);
  };

  const handleQuestionTypeChange = (idx, newType) => {
    formik.setFieldValue(`questions[${idx}].question_type`, newType);
    formik.setFieldValue(`questions[${idx}].alternatives`, buildEmptyAlternatives(newType));
  };

  const handleCorrectChange = (qIdx, altIdx) => {
    const alts = formik.values.questions[qIdx].alternatives.map((alt, i) => ({
      ...alt,
      is_correct: i === altIdx
    }));
    formik.setFieldValue(`questions[${qIdx}].alternatives`, alts);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!formik.values.title || !formik.values.taxonomy_id) {
      toast.error('Volte à etapa 1 e preencha título e tema.');
      navigate(backTarget);
      return;
    }
    if (formik.values.questions.length === 0) {
      toast.error('Adicione pelo menos uma questão.');
      return;
    }
    if (formik.values.questions.some(q => !q.description.trim())) {
      toast.error('Preencha o enunciado de todas as questões.');
      return;
    }
    if (formik.values.questions.some(q => q.alternatives.filter(alt => alt.description.trim()).length < 2)) {
      toast.error('Cada questão precisa de pelo menos 2 alternativas preenchidas.');
      return;
    }
    formik.handleSubmit();
  };

  return (
    <div className="page page--wrap">
      <div className="page__col">
        <form
          className="form form--step-by-step"
          method="post"
          onSubmit={handleSubmit}
        >
          <div className="page__content mb-12 animate-fade-in">
            <div className="new-questions__header">
              <h2 className="new-questions__title">Questões</h2>
            </div>

            <div className="flex animate-fade-in flex-col gap-4">
              {formik.values.questions.map((question, qIndex) => (
                <div
                  key={qIndex}
                  className="flex flex-col gap-4 rounded-xl border border-solid border-neutral-lightest p-4"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-neutral-dark">Questão {qIndex + 1}</span>
                    {formik.values.questions.length > 1 && (
                      <Button
                        type="button"
                        variant="outline-danger"
                        size="xs"
                        onClick={() => handleRemove(qIndex)}
                      >
                        <FiTrash2 /> Remover
                      </Button>
                    )}
                  </div>

                  <Field>
                    <FieldLabel>* Enunciado</FieldLabel>
                    <Textarea
                      rows={3}
                      placeholder="Escreva a pergunta..."
                      value={question.description}
                      onChange={e => formik.setFieldValue(`questions[${qIndex}].description`, e.target.value)}
                    />
                  </Field>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field>
                      <FieldLabel>Tipo</FieldLabel>
                      <SelectNative
                        value={question.question_type}
                        onChange={e => handleQuestionTypeChange(qIndex, e.target.value)}
                      >
                        {QUESTION_TYPES.map(qt => (
                          <option
                            key={qt.value}
                            value={qt.value}
                          >
                            {qt.label}
                          </option>
                        ))}
                      </SelectNative>
                    </Field>

                    <Field>
                      <FieldLabel>Tema (opcional)</FieldLabel>
                      <SelectNative
                        value={question.taxonomy_id}
                        onChange={e => formik.setFieldValue(`questions[${qIndex}].taxonomy_id`, e.target.value)}
                      >
                        <option value="">Mesmo do desafio</option>
                        {taxonomies.map(t => (
                          <option
                            key={t.id}
                            value={t.id}
                          >
                            {t.name}
                          </option>
                        ))}
                      </SelectNative>
                    </Field>
                  </div>

                  <Field>
                    <FieldLabel>* Alternativas</FieldLabel>
                    <div className="flex flex-col gap-2">
                      {question.alternatives.map((alt, altIndex) => (
                        <div
                          key={altIndex}
                          className="flex items-center gap-3"
                        >
                          <input
                            type="radio"
                            name={`correct-${qIndex}`}
                            checked={alt.is_correct}
                            onChange={() => handleCorrectChange(qIndex, altIndex)}
                            className="h-4 w-4 cursor-pointer accent-primary"
                            aria-label={`Marcar alternativa ${String.fromCharCode(65 + altIndex)} como correta`}
                          />
                          <Input
                            type="text"
                            placeholder={`Alternativa ${String.fromCharCode(65 + altIndex)}`}
                            value={alt.description}
                            onChange={e => formik.setFieldValue(`questions[${qIndex}].alternatives[${altIndex}].description`, e.target.value)}
                            disabled={question.question_type === 'true_false'}
                          />
                        </div>
                      ))}
                    </div>
                    <FieldDescription>Selecione o radio da alternativa correta.</FieldDescription>
                  </Field>
                </div>
              ))}

              <Button
                type="button"
                variant="outline-dashed"
                size="md"
                fullWidth
                onClick={handleAdd}
              >
                <FiPlus /> Adicionar questão
              </Button>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              size="md"
              wide
              onClick={() => navigate(backTarget)}
            >
              Voltar
            </Button>
            <Button
              type="submit"
              variant="default"
              size="md"
              wide
              disabled={submitting}
            >
              {submitting ? 'Salvando...' : isEditing ? 'Salvar alterações' : 'Criar desafio'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
